<template>
    <div class="panel">
        <template v-if="getRoundLoadedTime !== false && getRoundStartDate !== false">
            <CountdownTimer
                title="Round starting in"
                :start-date="getRoundLoadedTime"
                :end-date="getRoundStartDate"
                :primary-colour="getQuizThemeColours.primary"
                :secondary-colour="getQuizThemeColours.secondary"
            />
            <div class="text-center">
                <button v-if="quiz.automaticStart" @click="cancelTimer" class="btn btn-grey-200">
                    Cancel
                </button>
            </div>
        </template>

        <template v-else>
            <div class="flex flex-col items-center mb-4 sm:mb-7">
                <h3 class="text-md font-bold mb-7 tag">
                    <template v-if="!isAdmin">
                        <template v-if="!quiz.automaticStart">Please wait for the host to start</template>
                        <template v-else>Please wait for the quiz to start</template>
                    </template>
                    <template v-else>Waiting for players to join</template>
                </h3>
                <ParticipantCount
                    :participant-count="getScoreboardCount"
                />
            </div>
            <div ref="participants" class="relative before:block before:absolute before:bottom-0 before:bg-gradient-to-t before:from-white before:w-full before:h-1/5">
                <div @scroll="handleTableScroll" class="max-h-[568px] overflow-y-scroll table-scroll-wrapper pr-3">
                    <TransitionGroup name="list" tag="ul" v-if="getScoreboardCount > 0">
                        <li :key="participant?.participant_id ?? index"
                            v-for="(participant, index) in getWaitingRoomParticipants"
                            class="border p-2.5 rounded text-sm font-bold font-sans mb-2.5 flex justify-between items-center border-grey-200">
                                {{participant?.name}}
                        </li>
                    </TransitionGroup>
                </div>
            </div>
        </template>
    </div>
</template>
<script setup>
import CountdownTimer from "../Global/CountdownTimer.vue";
import ParticipantCount from "./ParticipantCount.vue";
import {QuizData} from "../../../composibles/quizData.js";
import {RoundData} from "../../../composibles/roundData.js";
import {useParticipantStore} from "../../../stores/participant.js";
import {ParticipantData} from "../../../composibles/participantData.js";
import {computed, onMounted, ref} from "vue";

const participantStore = useParticipantStore();

const {quiz, currentRound, getQuizThemeColours} = QuizData();
const {getRoundParticipants, getRoundStartDate, getRoundLoadedTime} = RoundData();
const {participant} = ParticipantData();

const participants = ref(null);

const props = defineProps({
    isAdmin: {
        type: Boolean,
        default: false
    }
})

const getScoreboardCount = computed(() => {
    return getWaitingRoomParticipants.value.length
});

const getWaitingRoomParticipants = computed(() => {
    const roundParticipants = [...(getRoundParticipants.value ?? [])];
    const currentParticipantId = participant.value?.participant_id ?? participant.value?.id;

    if (props.isAdmin || !currentParticipantId) {
        return roundParticipants;
    }

    const participantExists = roundParticipants.some((roundParticipant) => {
        return Number(roundParticipant?.participant_id ?? roundParticipant?.id) === Number(currentParticipantId);
    });

    if (participantExists) {
        return roundParticipants;
    }

    return [...roundParticipants, {
        participant_id: currentParticipantId,
        id: currentParticipantId,
        name: participant.value.name,
        answered_questions: [],
        status: 'active'
    }];
});

const loadWaitingRoomParticipants = async () => {
    if (!quiz.value?.id) {
        return;
    }

    try {
        await participantStore.loadParticipants({quizId: quiz.value.id});
    } catch (error) {
        console.error(error);
    }
};

const cancelTimer = async () => {
    if (quiz.value.automaticStart) {
        await participantStore.deleteRoundParticipant({
            quizId: quiz.value.id,
            roundId: currentRound.value.id,
            participantId: participant.value.id
        });

    } else if (props.isAdmin) {
        // Cancel round timer
    }
}

const handleTableScroll = (e) => {
    const tableWrapper  = e.target;
    const scrollTop    = tableWrapper.scrollTop;
    const scrollHeight = tableWrapper.scrollHeight;
    const clientHeight = tableWrapper.clientHeight;

    if (!participants.value) {
        return;
    }

    if (scrollTop + clientHeight >= scrollHeight) {
        participants.value.classList.add('before:hidden');
    } else {
        participants.value.classList.remove('before:hidden');
        participants.value.classList.add('before:visible');
    }
}

onMounted(async () => {
    await loadWaitingRoomParticipants();
});
</script>
